import { createPortal } from 'react-dom'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Music } from 'lucide-react'
import styles from './ordinal-audio-card.module.scss'

export function AudioArtworkTitle({
    title,
    testId = 'audio-artwork-title',
}: {
    title?: string
    testId?: string
}) {
    return title ? (
        <div className={styles.title} title={title} data-testid={testId}>
            {title}
        </div>
    ) : null
}

/** Shared artwork and caption; each host retains its own playback and media actions. */
export default function AudioArtworkCard({
    title,
    previewUrl,
    palette = 'purple',
    topControls,
    topLeft,
    overlay,
    stage,
    chromeLayer,
    movableChrome = false,
    children,
    style,
    testId = 'audio-artwork-card',
    titleTestId,
}: {
    title?: string
    previewUrl?: string
    palette?: 'purple' | 'orange'
    topControls?: React.ReactNode
    /** Top-left corner over the artwork (a music card's poster avatar). */
    topLeft?: React.ReactNode
    /** Drawn over the artwork's center, under the controls (e.g. a hover hint). */
    overlay?: React.ReactNode
    /** Drawn over the artwork and under the scrim (a video playing in place). */
    stage?: React.ReactNode
    /**
     * The dock's chrome layer while its own video is parked over this card
     * (hosted playback): the card's controls are drawn there, above the picture.
     */
    chromeLayer?: Element | null
    /**
     * The chrome may move to the dock's chrome layer and back. It is rendered
     * once into its own element, which is moved between the card and the layer,
     * so the player inside never remounts (a remount would drop the origin the
     * dock is parked over).
     */
    movableChrome?: boolean
    children: React.ReactNode
    style?: React.CSSProperties
    testId?: string
    titleTestId?: string
}) {
    const [artworkFailed, setArtworkFailed] = useState(false)
    const chrome = (
        <>
            <div className={styles.scrim} aria-hidden="true" />
            {overlay}
            {topLeft && <div className={styles.topLeft}>{topLeft}</div>}
            {topControls && (
                <div className={styles.topControls} data-post-embed-navigation-exempt="true">
                    {topControls}
                </div>
            )}
            <div className={styles.footer}>
                <AudioArtworkTitle title={title} testId={titleTestId} />
                {children}
            </div>
        </>
    )
    useEffect(() => setArtworkFailed(false), [previewUrl])

    const chromeHomeRef = useRef<HTMLDivElement>(null)
    // Created when movable chrome turns on, not only at mount: a card can switch
    // to a player that hosts its picture while it stays mounted (an ON AIR card
    // whose song ended). That switch remounts the strip once, before anything plays.
    const chromeHost = useMemo<HTMLDivElement | null>(
        () =>
            movableChrome && typeof document !== 'undefined' ? document.createElement('div') : null,
        [movableChrome],
    )
    useLayoutEffect(() => {
        if (!chromeHost) return
        chromeHost.className = chromeLayer ? styles.chromeInDock : styles.chromeHome
        const parent = chromeLayer || chromeHomeRef.current
        if (parent && chromeHost.parentNode !== parent) parent.appendChild(chromeHost)
    }, [chromeHost, chromeLayer])
    useEffect(() => () => chromeHost?.remove(), [chromeHost])

    return (
        // The dock-owned placeholder stands in for this whole card, not for the transport
        // strip on its scrim — see [data-dock-placeholder-host] in audio.module.scss.
        <div
            className={styles.card}
            style={style}
            data-dock-placeholder-host=""
            data-testid={testId}
        >
            {previewUrl && !artworkFailed ? (
                <img
                    className={styles.artwork}
                    src={previewUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => setArtworkFailed(true)}
                />
            ) : (
                <div
                    className={cn(
                        styles.placeholder,
                        palette === 'orange' && styles.placeholderOrange,
                    )}
                    aria-hidden="true"
                >
                    <Music className={styles.placeholderIcon} />
                </div>
            )}
            {stage}
            {chromeHost ? (
                <>
                    <div ref={chromeHomeRef} className={styles.chromeHome} />
                    {createPortal(chrome, chromeHost)}
                </>
            ) : chromeLayer ? (
                createPortal(<div className={styles.chromeInDock}>{chrome}</div>, chromeLayer)
            ) : (
                chrome
            )}
        </div>
    )
}
